import React from "react";
import { useFormContext } from "react-hook-form";

import { FormField } from "components/form/FormField/FormField";

import style from "../AddVaccineForm.module.css";

export const TextField = ({
  name,
  label,
  placeholder = "",
  type = "text",
  inputMode,
  wrapperClass,
  disabled = false,
  value: valueProp,
  required = false,
  ariaRequired = false,
  onChange,
  pattern,
}) => {
  const { register, watch } = useFormContext();

  const watchedValue = watch(name);
  const registered = register(name);
  // Callers that need to shape the keystroke before it is stored — stripping characters
  // the field cannot hold, for instance — pass onChange. It runs first, on the same
  // event, so what it writes to the target is what react-hook-form then reads. This
  // component owns the register() call, so a caller cannot supply one itself: doing so
  // spreads register()'s props onto TextField, where they are silently dropped.
  const handleChange = (event) => {
    onChange?.(event);

    return registered.onChange(event);
  };
  // Whether the input is controlled follows the `value` prop, never `disabled`. It used
  // to follow `disabled`, so a field that becomes read-only while open — the dose, once
  // a product supplies one — switched from uncontrolled to controlled mid-life and React
  // warned about exactly that. Only the two display-only fields pass `value`, and both
  // are disabled for their whole lifetime, so neither flips.
  //
  // A disabled input still registered with react-hook-form keeps its value in form
  // state: `disabled` here is a DOM attribute, not register's `disabled` option, which
  // is the one that blanks the value.
  const isDisplayOnly = valueProp !== undefined;
  const inputProps = isDisplayOnly
    ? { value: valueProp ?? watchedValue ?? "", disabled: true }
    : {
        ...registered,
        disabled,
        ...(onChange && { onChange: handleChange }),
      };

  // No asterisk on a field the user cannot change: marking it required is a demand they
  // cannot act on. The Dose is the case in point — derived from the product for RSV and
  // Pneumovax and locked, yet labelled "Dose *". Display-only counts as uneditable too,
  // even where `disabled` was not passed, because that branch hard-disables the input.
  const isEditable = !disabled && !isDisplayOnly;

  const input = (
    <input
      type={type}
      className="form-control"
      placeholder={placeholder}
      inputMode={inputMode}
      pattern={pattern}
      {...inputProps}
      aria-required={required || ariaRequired ? "true" : undefined}
    />
  );

  return (
    <FormField name={name}>
      <label className={style.pregnancyFormLabel}>
        {label}
        {required && isEditable ? " *" : ""}
      </label>
      {wrapperClass ? (
        <div className={style[wrapperClass]}>{input}</div>
      ) : (
        input
      )}
    </FormField>
  );
};

export const TextAreaField = ({
  name,
  label,
  placeholder = "",
  rows = 3,
  maxLength,
}) => {
  const { register, watch } = useFormContext();

  const value = watch(name) || "";
  const limit = maxLength;

  return (
    <FormField name={name}>
      <label className={style.pregnancyFormLabel}>{label}</label>
      <div className={style.textAreaWrapper}>
        <textarea
          rows={rows}
          placeholder={placeholder}
          className="form-control"
          maxLength={limit}
          {...register(name)}
        />
        {limit && (
          <div className={style.charCounter}>
            {value.length}/{limit}
          </div>
        )}
      </div>
    </FormField>
  );
};
